import { defineStore } from "pinia";
import api, { resolveMediaUrl } from "../api";
import { isAuthRejection } from "../utils/authErrors";

const ACCESS_KEY = "eco_access_token";
const REFRESH_KEY = "eco_refresh_token";

// Вне state стора: событие storage срабатывает только в ДРУГИХ вкладках,
// поэтому зацикливания на себе не будет; флаг просто защищает от повторной подписки.
let crossTabSyncBound = false;

// Проактивный рефреш access-токена - вне state стора, как и crossTabSyncBound,
// таймер не сериализуемое состояние. Без этого access-токен (живёт 15 минут,
// backend/app/core/config.py's access_token_exp_minutes) обновлялся только
// реактивно на 401 внутри axios-интерцептора (api.ts) - что покрывает все
// обычные запросы, но НЕ запросы тайлов MapLibre (transformRequest сам
// прикладывает Bearer-заголовок для pois_admin/buildings_admin, см.
// MapView.vue, но идёт мимо axios через собственный fetch движка карты).
// В сессии редактирования карты дольше 15 минут это роняло все admin-тайлы
// с 401 до конца сессии, не восстанавливаясь - см. GlitchTip-находку
// /internal/tile-auth 2026-09-04.
let refreshTimer: ReturnType<typeof setTimeout> | null = null;
let inflightRefresh: Promise<RefreshResult> | null = null;
const REFRESH_RETRY_MS = 10_000;
type RefreshResult = "ok" | "rejected" | "transient";

// Читает "exp" (unix-секунды) из payload JWT без проверки подписи - только
// чтобы понять, когда токен истечёт и заранее его обновить; сама подпись
// всё равно проверяется на бэкенде при каждом запросе.
function decodeJwtExpiryMs(token: string): number | null {
    try {
        const payload = token.split(".")[1];
        const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
        const exp = JSON.parse(json)?.exp;
        return typeof exp === "number" ? exp * 1000 : null;
    } catch {
        return null;
    }
}

export interface AuthIdentity {
    provider: string;
    username: string | null;
    photo_url: string | null;
}

// Форма ответа GET /auth/me - хранится здесь, а не в общем файле типов,
// так как весь payload целиком использует только setProfile.
export interface MeResponse {
    id: number;
    email: string | null;
    email_verified_at: string | null;
    phone: string | null;
    phone_verified_at: string | null;
    roles: string[];
    display_name: string | null;
    avatar_url: string | null;
    emergency_contact_name: string | null;
    emergency_contact_phone: string | null;
    username: string;
    first_name: string | null;
    last_name: string | null;
    gender: string | null;
    birth_date: string | null;
    profile_visibility: string;
    terms_accepted_at: string | null;
    privacy_policy_version: string | null;
    terms_of_service_version: string | null;
    data_consent_version: string | null;
    identities: AuthIdentity[];
}

export const useAuthStore = defineStore("auth", {
    state: () => ({
        accessToken: localStorage.getItem(ACCESS_KEY) || "",
        refreshToken: localStorage.getItem(REFRESH_KEY) || "",
        userId: null as number | null,
        email: "",
        emailVerifiedAt: "",
        phone: "",
        phoneVerifiedAt: "",
        roles: [] as string[],
        displayName: "",
        avatarUrl: "",
        emergencyContactName: "",
        emergencyContactPhone: "",
        username: "",
        firstName: "",
        lastName: "",
        gender: "",
        birthDate: "",
        profileVisibility: "private",
        termsAcceptedAt: "",
        identities: [] as AuthIdentity[],
    }),
    getters: {
        isAuthenticated: (state) => Boolean(state.accessToken),
        isAdmin: (state) => state.roles.includes("admin"),
        // "Модератор приветов" - публикация/правка #ПриветEcoway. admin
        // включён (require_hello_admin на бэкенде тот же OR).
        isHelloAdmin: (state) => state.roles.includes("admin") || state.roles.includes("hello_admin"),
    },
    actions: {
        setTokens(access: string, refresh: string) {
            this.accessToken = access;
            this.refreshToken = refresh;
            localStorage.setItem(ACCESS_KEY, access);
            localStorage.setItem(REFRESH_KEY, refresh);
            this.scheduleTokenRefresh();
        },
        // Таймер на "истечение минус минута" (JWT exp) - вызывается из
        // setTokens (после логина/рефреша) и один раз из App.vue при
        // монтировании (там же, где bindCrossTabSync), чтобы уже
        // сохранённый в localStorage токен от предыдущей сессии тоже
        // получил расписание, а не только свежевыданный.
        scheduleTokenRefresh() {
            if (refreshTimer) {
                clearTimeout(refreshTimer);
                refreshTimer = null;
            }
            if (!this.accessToken) return;
            const expMs = decodeJwtExpiryMs(this.accessToken);
            if (expMs === null) return;
            const delay = Math.max(expMs - Date.now() - 60_000, 5_000);
            refreshTimer = setTimeout(() => this.refreshAccessToken(), delay);
        },
        // "ok" - токены обновлены; "rejected" - сервер отверг refresh-токен
        // (401/403), сессия стёрта; "transient" - сеть/5xx, токены НЕ тронуты,
        // повтор запланирован. Параллельные вызовы (таймер + интерцептор + другой
        // код) делят один запрос.
        async refreshAccessToken(): Promise<RefreshResult> {
            if (!this.refreshToken) return "rejected";
            if (inflightRefresh) return inflightRefresh;
            inflightRefresh = (async (): Promise<RefreshResult> => {
                try {
                    // Refresh-токен одноразовый (ротация на бэкенде): берём самый свежий из
                    // localStorage - другая вкладка могла уже обменять наш in-memory токен.
                    const current = localStorage.getItem(REFRESH_KEY) || this.refreshToken;
                    const res = await api.post("/auth/refresh", { refresh_token: current });
                    // setTokens сам перепланирует следующий рефреш.
                    this.setTokens(res.data.access_token, res.data.refresh_token);
                    return "ok";
                } catch (e) {
                    if (isAuthRejection(e)) {
                        this.clear();
                        return "rejected";
                    }
                    // Сеть ещё не поднялась после сна/разблокировки или бэкенд
                    // рестартует - refresh-токен при этом валиден до 7 дней.
                    this.scheduleRefreshRetry();
                    return "transient";
                } finally {
                    inflightRefresh = null;
                }
            })();
            return inflightRefresh;
        },
        // Выход: просим бэкенд отозвать цепочку refresh-токенов (best-effort, сеть не
        // должна мешать выйти) и стираем локальную сессию.
        logout() {
            const refresh = this.refreshToken;
            this.clear();
            if (refresh) api.post("/auth/logout", { refresh_token: refresh }).catch(() => {});
        },
        scheduleRefreshRetry() {
            if (refreshTimer) clearTimeout(refreshTimer);
            refreshTimer = setTimeout(() => this.refreshAccessToken(), REFRESH_RETRY_MS);
        },
        clear() {
            if (refreshTimer) {
                clearTimeout(refreshTimer);
                refreshTimer = null;
            }
            this.accessToken = "";
            this.refreshToken = "";
            this.userId = null;
            this.email = "";
            this.emailVerifiedAt = "";
            this.phone = "";
            this.phoneVerifiedAt = "";
            this.roles = [];
            this.displayName = "";
            this.avatarUrl = "";
            this.emergencyContactName = "";
            this.emergencyContactPhone = "";
            this.username = "";
            this.firstName = "";
            this.lastName = "";
            this.gender = "";
            this.birthDate = "";
            this.profileVisibility = "private";
            this.termsAcceptedAt = "";
            this.identities = [];
            localStorage.removeItem(ACCESS_KEY);
            localStorage.removeItem(REFRESH_KEY);
        },
        setProfile(me: MeResponse) {
            this.userId = me.id ?? null;
            this.email = me.email || "";
            this.emailVerifiedAt = me.email_verified_at || "";
            this.phone = me.phone || "";
            this.phoneVerifiedAt = me.phone_verified_at || "";
            this.roles = me.roles || [];
            this.displayName = me.display_name || "";
            // Свои аватары приходят относительным путём бэкенда - резолвим
            // в абсолютный URL один раз здесь, чтобы всем потребителям
            // auth.avatarUrl можно было просто биндить его напрямую.
            this.avatarUrl = resolveMediaUrl(me.avatar_url) || "";
            this.emergencyContactName = me.emergency_contact_name || "";
            this.emergencyContactPhone = me.emergency_contact_phone || "";
            this.username = me.username || "";
            this.firstName = me.first_name || "";
            this.lastName = me.last_name || "";
            this.gender = me.gender || "";
            this.birthDate = me.birth_date || "";
            this.profileVisibility = me.profile_visibility || "private";
            this.termsAcceptedAt = me.terms_accepted_at || "";
            this.identities = me.identities || [];
        },
        // Держит сессию синхронной между вкладками одного источника. Вызывать один раз из App.vue при монтировании.
        bindCrossTabSync() {
            if (crossTabSyncBound) return;
            crossTabSyncBound = true;
            // Вкладка/ноутбук/телефон после долгого простоя: setTimeout мог
            // не отработать вовремя (заморожен) - при возврате и появлении сети
            // сами проверяем, не пора ли обновить access-токен.
            const refreshIfStale = () => {
                if (!this.refreshToken) return;
                const expMs = decodeJwtExpiryMs(this.accessToken);
                if (expMs === null || expMs - Date.now() < 60_000) this.refreshAccessToken();
            };
            window.addEventListener("online", refreshIfStale);
            document.addEventListener("visibilitychange", () => {
                if (document.visibilityState === "visible") refreshIfStale();
            });
            window.addEventListener("storage", (event) => {
                if (event.key !== ACCESS_KEY) return;
                if (!event.newValue) {
                    this.clear();
                    return;
                }
                if (event.newValue === this.accessToken) return;
                this.accessToken = event.newValue;
                this.refreshToken = localStorage.getItem(REFRESH_KEY) || "";
                this.scheduleTokenRefresh();
                api
                    .get("/auth/me")
                    .then((res) => this.setProfile(res.data))
                    .catch((e) => {
                        if (isAuthRejection(e)) this.clear();
                    });
            });
        },
    },
});
